import { useCoreApiClient } from "@api/use-core-api-client";
import { SdbMemberTypes } from "@custom-types/member-types";
import { SphereDashboardAPITypes } from "@stellar/api-logic";
import {
  companyMembersSelector,
  fetchingMembersFlagsSelector,
} from "@store/members/members-selector";
import { fetchCompanyMembers } from "@store/members/members-slice-thunk";
import {
  fetchingProjectsFlagsSelector,
  projectMembersSelector,
} from "@store/projects/projects-selector";
import { selectedSdbCompanyIdSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchingUserFlagsSelector } from "@store/user/user-selector";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useEffect, useMemo } from "react";
import { fetchTeams } from "@store/teams/teams-slice-thunk";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";

interface UseMembers {
  /** List of company members */
  companyMembers: SdbMemberTypes[];

  /** Whether the company members are being fetched from the backend */
  isFetchingCompanyMembers: boolean;

  /** List of project members */
  projectMembers: SphereDashboardAPITypes.IProjectMemberBase[];

  /** Whether the project members are being fetched from the backend */
  isFetchingProjectMembers: boolean;
}

/**
 * Custom hook that returns the company and project members and their fetching status.
 * For company members, only if current user has permission to view the company members list the members are returned.
 * If the user does not have permission then it returns an empty array.
 */
export function useMembers(): UseMembers {
  const dispatch = useAppDispatch();
  const coreApiClient = useCoreApiClient();
  const companyId = useAppSelector(selectedSdbCompanyIdSelector);
  const { isFetchingCurrentUser } = useAppSelector(fetchingUserFlagsSelector);
  const companyMembers = useAppSelector(companyMembersSelector);
  const { isFetchingCompanyMembers } = useAppSelector(
    fetchingMembersFlagsSelector
  );
  const projectMembers = useAppSelector(projectMembersSelector);
  const { isFetchingProjects, isFetchingProjectContext } = useAppSelector(
    fetchingProjectsFlagsSelector
  );
  const { canFetchTeams } = useHasUserValidRoleCompanyLevel();
  const { canViewCompanyMembers } = useHasUserValidFeatureCompanyLevel();
  const { canViewAllProjectMembers } = useHasUserValidPermissionProjectLevel();

  // Whether the current user has the permission to view all company members with access to the project
  const hasPermissionToViewMembers = useMemo(() => {
    // Deny access while the current user and selected project data are being fetched
    // This data is required to check the permission
    if (
      isFetchingCurrentUser ||
      isFetchingProjects ||
      isFetchingProjectContext
    ) {
      return false;
    }

    return canViewAllProjectMembers || canViewCompanyMembers;
  }, [
    canViewCompanyMembers,
    canViewAllProjectMembers,
    isFetchingCurrentUser,
    isFetchingProjectContext,
    isFetchingProjects,
  ]);

  const members = useMemo(() => {
    return hasPermissionToViewMembers ? companyMembers : [];
  }, [companyMembers, hasPermissionToViewMembers]);

  // If the user has permission to view members then fetch members just in case they have not been
  // fetched yet.
  // Also fetch teams if the user has permissions.
  useEffect(() => {
    if (hasPermissionToViewMembers && companyId) {
      void dispatch(
        fetchCompanyMembers({
          coreApiClient,
          companyId,
        })
      );

      if (canFetchTeams) {
        void dispatch(fetchTeams({ coreApiClient }));
      }
    }
  }, [
    companyId,
    coreApiClient,
    dispatch,
    hasPermissionToViewMembers,
    canFetchTeams,
  ]);

  return {
    companyMembers: members,
    isFetchingCompanyMembers,
    projectMembers,
    isFetchingProjectMembers: isFetchingProjects,
  };
}
